import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { ESourceAccessGate, useESourceAccess } from "./esourceAccess";
import {
  applyFormBuilderServerAssist,
  assistCanvasDidChange,
  buildCanvasSnapshotForAssist,
  generateESourceFormFromPrompt,
  looksLikeAssistEditIntent,
  summarizeAssistFieldChanges,
} from "./esourceAiAssist";
import {
  buildFormFromDocumentPrompt,
  extractAssistDocumentText,
} from "./esourceAssistDocumentImport";
import {
  applyFormLibraryTemplate,
  getFormLibraryTemplates,
} from "./esourceFormLibrary";
import FaqAssistMark, { FaqAssistAiIcon } from "../FAQ/FaqAssistMark";
import FaqAssistUnavailableModal from "../FAQ/FaqAssistUnavailableModal";
import ESourceFormAssistPane from "./ESourceFormAssistPane";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildEsourceFormBuilderTourSteps } from "./esourceFormBuilderTourSteps";
import {
  hasCompletedEsourceFormBuilderTour,
  markEsourceFormBuilderTourCompleted,
} from "./esourceFormBuilderTourStorage";
import {
  getFormAssistSession,
  newFormAssistSessionId,
  readFormAssistSessions,
  upsertFormAssistSession,
} from "./esourceFormAssistChatStore";
import {
  askFormBuilderAssist,
  fetchFaqAiStatus,
} from "../../../services/faqService";
import { submitFeedbackToClickUp } from "../../../services/clickupService";
import {
  FIELD_TYPE_GROUPS,
  FILL_ACCESS_INTERNAL,
  applyFieldDefaults,
  buildSchemaJson,
  collectRequiredMissing,
  collectFieldValidationIssues,
  createEmptyLogicItem,
  duplicateFieldInList,
  ensureUniqueFieldName,
  evaluateLogicVisibility,
  findFieldReferences,
  isLayoutField,
  normalizeFormDescription,
  normalizeFormHeader,
  normalizeFormSettings,
  FORM_DESCRIPTION_MAX_CHARS,
  typeMeta,
  validateTemplateDesign,
} from "./esourceFieldTypes";
import {
  emptyField,
  fetchTemplateById,
  parseSchemaJson,
  saveTemplate,
  createDraftVersion,
} from "./esourceService";
import { confirmAndPublishTemplate } from "./esourcePublishGate";
import ESourceFieldPropertiesForm from "./ESourceFieldPropertiesForm";
import ESourceConditionalLogicBuilder from "./ESourceConditionalLogicBuilder";
import { ESourceFormFields } from "./esourceFormRender";
import Dropdown from "../../common/dropDown";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import "./ESourcePages.scss";

const OTHER_FORM_TYPE = "Other";

/** HTML5 DnD payload for Field types → canvas. */
const DND_FIELD_TYPE = "text/esource-field-type";
const DND_TYPE_PREFIX = "esource-type:";

/** Save / Form type options for this release. */
const FORM_TYPE_VALUES = [
  "Clinical Trial",
  "Inventory/Equipment/Logistics",
  "Laboratory",
  "Pharmacy/ePSF",
  OTHER_FORM_TYPE,
];

const LIBRARY_FORM_TYPES = FORM_TYPE_VALUES.filter((c) => c !== OTHER_FORM_TYPE);

const FORM_TYPE_OPTIONS = FORM_TYPE_VALUES.map((c) => ({
  value: c,
  label: c,
}));

function mapLegacyScopeToFormType(value) {
  const v = String(value || "").trim();
  if (!v) return OTHER_FORM_TYPE;
  if (FORM_TYPE_VALUES.includes(v)) return v;
  if (/pharmacy|epsf/i.test(v)) return "Pharmacy/ePSF";
  if (/^lab$/i.test(v) || /laboratory/i.test(v)) return "Laboratory";
  if (/clinical\s*trial/i.test(v)) return "Clinical Trial";
  if (/inventory|equipment|logistics/i.test(v)) {
    return "Inventory/Equipment/Logistics";
  }
  return OTHER_FORM_TYPE;
}

function versionRowId(version) {
  return String(
    version?.FormTemplateVersionId ||
      version?.formTemplateVersionId ||
      "",
  ).trim();
}

function formatVersionOptionLabel(version) {
  const no = version?.VersionNo;
  const status = String(version?.Status || "").trim();
  const ver = no != null && no !== "" ? `v${no}` : "v?";
  if (/draft/i.test(status)) return `Draft ${ver}`;
  if (/published/i.test(status)) return `Published ${ver}`;
  return status ? `${status} ${ver}` : ver;
}

function sortVersionsDesc(versions = []) {
  return [...versions].sort(
    (a, b) => Number(b.VersionNo || 0) - Number(a.VersionNo || 0),
  );
}

function pickDefaultPreviewVersion(versions = []) {
  const list = sortVersionsDesc(versions);
  const draft = list.find((v) => /draft/i.test(String(v.Status || "")));
  if (draft) return draft;
  return (
    list.find((v) => /published/i.test(String(v.Status || ""))) || list[0] || null
  );
}

const ESourceTemplateBuilder = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";
  const forcePreview = searchParams.get("mode") === "preview";
  const navigate = useNavigate();
  const { loading, canDesign, userData } = useESourceAccess();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [formTemplateId, setFormTemplateId] = useState(isNew ? "" : id);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [moduleScope, setModuleScope] = useState("Pharmacy/ePSF");
  const [draftVersionNo, setDraftVersionNo] = useState(null);
  const [publishedVersionNo, setPublishedVersionNo] = useState(null);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [previewVersionId, setPreviewVersionId] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [saveIntent, setSaveIntent] = useState(null); // null | "draft" | "publish"
  const [saveCode, setSaveCode] = useState("");
  const [saveName, setSaveName] = useState("");
  const [saveModuleScope, setSaveModuleScope] = useState("Pharmacy/ePSF");
  const [fields, setFields] = useState([]);
  const [formHeader, setFormHeader] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const fillAccess = FILL_ACCESS_INTERNAL;
  const [formSettings, setFormSettings] = useState(() =>
    normalizeFormSettings({ layout: "two-column" }),
  );
  const [logic, setLogic] = useState([]);
  const [formSettingsOpen, setFormSettingsOpen] = useState(false);
  const [introPromptIntent, setIntroPromptIntent] = useState(null); // null | "preview" | "draft"
  const formHeaderInputRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [fieldSettingsOpen, setFieldSettingsOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [assistMessages, setAssistMessages] = useState([]);
  const [assistSessionId, setAssistSessionId] = useState(() =>
    newFormAssistSessionId(),
  );
  const [assistRecentSessions, setAssistRecentSessions] = useState([]);
  const [leftTool, setLeftTool] = useState(isNew ? "library" : "details"); // library | details | assist
  const [libraryCategory, setLibraryCategory] = useState("Pharmacy/ePSF");
  const [selectedLibraryId, setSelectedLibraryId] = useState(null);
  const selectedLibraryCardRef = useRef(null);
  const dragFieldIdRef = useRef(null);
  const dragPaletteTypeRef = useRef(null);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const [canvasPaletteOver, setCanvasPaletteOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewValues, setPreviewValues] = useState({});
  const [previewErrors, setPreviewErrors] = useState([]);
  const [previewSubmitMsg, setPreviewSubmitMsg] = useState("");
  const [assistUnavailableOpen, setAssistUnavailableOpen] = useState(false);
  const [assistUnavailableMessage, setAssistUnavailableMessage] = useState("");
  const [assistSupportSubmitting, setAssistSupportSubmitting] = useState(false);
  const [assistStatus, setAssistStatus] = useState(null);
  const [showFormBuilderTour, setShowFormBuilderTour] = useState(false);
  const [designIssues, setDesignIssues] = useState([]);
  const designValidationActiveRef = useRef(false);

  const libraryTemplates = useMemo(() => getFormLibraryTemplates(), []);
  const libraryCategories = useMemo(
    () =>
      LIBRARY_FORM_TYPES.filter((c) =>
        libraryTemplates.some((t) => t.category === c),
      ),
    [libraryTemplates],
  );
  const libraryCategoryOptions = useMemo(
    () => libraryCategories.map((c) => ({ value: c, label: c })),
    [libraryCategories],
  );
  const filteredLibrary = useMemo(
    () => libraryTemplates.filter((t) => t.category === libraryCategory),
    [libraryTemplates, libraryCategory],
  );

  const designErrorsByField = useMemo(() => {
    const map = new Map();
    designIssues.forEach((issue) => {
      if (!issue?.fieldId) return;
      const list = map.get(issue.fieldId) || [];
      list.push(issue.message);
      map.set(issue.fieldId, list);
    });
    return map;
  }, [designIssues]);

  const formLevelDesignErrors = useMemo(
    () =>
      designIssues
        .filter((issue) => !issue?.fieldId)
        .map((issue) => issue.message),
    [designIssues],
  );

  useEffect(() => {
    if (!designValidationActiveRef.current) return;
    const next = validateTemplateDesign(fields, logic);
    setDesignIssues(next);
    if (!next.length) designValidationActiveRef.current = false;
  }, [fields, logic]);

  useEffect(() => {
    if (!selectedLibraryId || leftTool !== "library") return;
    const el = selectedLibraryCardRef.current;
    if (!el) return;
    const rail = el.closest(".esource-builder-readymade");
    if (!rail) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }
    const elRect = el.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    const pad = 8;
    if (elRect.top < railRect.top + pad) {
      rail.scrollBy({
        top: elRect.top - railRect.top - pad,
        behavior: "smooth",
      });
    } else if (elRect.bottom > railRect.bottom - pad) {
      rail.scrollBy({
        top: elRect.bottom - railRect.bottom + pad,
        behavior: "smooth",
      });
    }
  }, [selectedLibraryId, libraryCategory, leftTool, filteredLibrary]);

  const schemaJson = useMemo(
    () =>
      buildSchemaJson({
        fields,
        formHeader,
        formDescription,
        fillAccess,
        settings: formSettings,
        logic,
      }),
    [fields, formHeader, formDescription, fillAccess, formSettings, logic],
  );

  const selected = useMemo(
    () => fields.find((f) => f.id === selectedId) || null,
    [fields, selectedId],
  );

  const notify = useCallback(
    (title, message, type = "info") => {
      showAlert({ title, message, type });
    },
    [showAlert],
  );

  const load = useCallback(async () => {
    if (isNew) {
      setIsReadOnly(false);
      return;
    }
    showLoader();
    try {
      const res = await fetchTemplateById(id);
      if (!res.success || !res.template) {
        notify("Template", res.message || "Template not found.", "danger");
        return;
      }
      setFormTemplateId(res.template.FormTemplateId);
      setCode(res.template.Code || "");
      setName(res.template.Name || "");
      setModuleScope(mapLegacyScopeToFormType(res.template.ModuleScope));

      const versions = sortVersionsDesc(res.versions || []);
      const draft = versions.find((v) => v.Status === "Draft") || null;
      const published =
        versions.find((v) => v.Status === "Published") || null;

      setTemplateVersions(versions);
      setDraftVersionNo(draft?.VersionNo != null ? draft.VersionNo : null);
      setPublishedVersionNo(
        published?.VersionNo != null
          ? published.VersionNo
          : res.template.PublishedVersionNo ?? null,
      );

      const previewOnly = forcePreview || (!draft && !!published);
      setIsReadOnly(previewOnly);

      if (!draft && !published) {
        notify("Template", "No form version found to open.", "warning");
        return;
      }

      if (!previewOnly && !draft && published) {
        navigate(
          `/esource/templates/${res.template.FormTemplateId}?mode=preview`,
          { replace: true },
        );
        return;
      }

      // Preview prefers draft when present (matches Edit); else published.
      const source = previewOnly
        ? pickDefaultPreviewVersion(versions)
        : draft;
      const parsed = parseSchemaJson(source?.SchemaJson);
      setFields(parsed.fields);
      setFormHeader(parsed.formHeader || "");
      setFormDescription(parsed.formDescription || "");
      setFormSettings(normalizeFormSettings(parsed.settings));
      setLogic(Array.isArray(parsed.logic) ? parsed.logic : []);
      if (parsed.fields[0]) setSelectedId(parsed.fields[0].id);
      else setSelectedId(null);
      setFieldSettingsOpen(false);
      if (previewOnly) {
        setPreviewVersionId(versionRowId(source));
        setPreviewValues(applyFieldDefaults(parsed.fields, {}));
        setPreviewErrors([]);
        setPreviewSubmitMsg("");
        if (forcePreview) setPreviewOpen(true);
      } else {
        setPreviewVersionId("");
      }
    } finally {
      hideLoader();
    }
  }, [
    id,
    isNew,
    forcePreview,
    showLoader,
    hideLoader,
    notify,
    navigate,
  ]);

  useEffect(() => {
    if (!loading && canDesign) load();
  }, [loading, canDesign, load]);

  const updateField = (fieldId, patch) => {
    if (isReadOnly) return;
    setFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)),
    );
  };

  const clearCanvasDragState = () => {
    dragFieldIdRef.current = null;
    dragPaletteTypeRef.current = null;
    setDragOverFieldId(null);
    setCanvasPaletteOver(false);
  };

  const isPaletteDragActive = (e) => {
    if (dragPaletteTypeRef.current) return true;
    const types = Array.from(e?.dataTransfer?.types || []);
    return types.includes(DND_FIELD_TYPE);
  };

  const readPaletteType = (dt) => {
    if (dragPaletteTypeRef.current) return dragPaletteTypeRef.current;
    if (!dt) return null;
    const typed = dt.getData(DND_FIELD_TYPE);
    if (typed) return typed;
    const plain = dt.getData("text/plain") || "";
    if (plain.startsWith(DND_TYPE_PREFIX)) {
      return plain.slice(DND_TYPE_PREFIX.length);
    }
    return null;
  };

  const insertFieldAt = (type = "text", beforeId = null) => {
    if (isReadOnly) return null;
    const field = emptyField(type);
    setFields((prev) => {
      field.fieldName = ensureUniqueFieldName(
        field.fieldName || field.label,
        prev,
        field.id,
      );
      if (!beforeId) return [...prev, field];
      const idx = prev.findIndex((f) => f.id === beforeId);
      if (idx < 0) return [...prev, field];
      const copy = [...prev];
      copy.splice(idx, 0, field);
      return copy;
    });
    setSelectedId(field.id);
    return field;
  };

  const addField = (type = "text") => {
    if (isReadOnly) return;
    insertFieldAt(type, null);
  };

  const duplicateField = (fieldId) => {
    if (isReadOnly) return;
    setFields((prev) => {
      const next = duplicateFieldInList(prev, fieldId);
      const idx = prev.findIndex((f) => f.id === fieldId);
      const clone = next[idx + 1];
      if (clone) setSelectedId(clone.id);
      return next;
    });
  };

  const removeField = (fieldId) => {
    if (isReadOnly) return;
    const doRemove = () => {
      const refs = findFieldReferences(fieldId, fields, logic);
      if (refs.length) {
        setLogic((prev) =>
          (prev || [])
            .map((item) => ({
              ...item,
              rules: (item.rules || []).filter((r) => r.fieldId !== fieldId),
              actions: (item.actions || []).filter(
                (a) => a.targetId !== fieldId,
              ),
            }))
            .filter(
              (item) =>
                (item.rules || []).length > 0 &&
                (item.actions || []).length > 0,
            ),
        );
      }
      setFields((prev) => prev.filter((f) => f.id !== fieldId));
      if (selectedId === fieldId) {
        setSelectedId(null);
        setFieldSettingsOpen(false);
      }
    };

    const refs = findFieldReferences(fieldId, fields, logic);
    if (refs.length) {
      showAlert({
        title: "Delete field?",
        message:
          "This field is referenced by conditional logic or a calculated formula. Delete it anyway?",
        type: "warning",
        buttonText: "Delete",
        cancelText: "Cancel",
        onConfirm: doRemove,
      });
      return;
    }

    showAlert({
      title: "Delete field?",
      message: "Remove this field from the canvas?",
      type: "warning",
      buttonText: "Delete",
      cancelText: "Cancel",
      onConfirm: doRemove,
    });
  };

  const openFieldSettings = (fieldId) => {
    setSelectedId(fieldId);
    setFieldSettingsOpen(true);
  };

  const closeFieldSettings = () => {
    setFieldSettingsOpen(false);
  };

  const selectField = (fieldId) => {
    setSelectedId(fieldId);
  };

  const openPreview = () => {
    setPreviewValues(applyFieldDefaults(fields, {}));
    setPreviewErrors([]);
    setPreviewSubmitMsg("");
    setFieldSettingsOpen(false);
    setPreviewOpen(true);
  };

  const openSaveModal = (intent) => {
    designValidationActiveRef.current = false;
    setDesignIssues([]);
    setSaveCode(code);
    setSaveName(name);
    setSaveModuleScope(mapLegacyScopeToFormType(moduleScope));
    setSaveIntent(intent);
  };

  const focusFormIntroEditor = () => {
    setFormSettingsOpen(true);
    window.requestAnimationFrame(() => {
      formHeaderInputRef.current?.focus?.();
    });
  };

  const isFormIntroIncomplete = () =>
    !String(formHeader || "").trim() || !String(formDescription || "").trim();

  const reportDesignValidation = (issues) => {
    if (!issues?.length) {
      designValidationActiveRef.current = false;
      setDesignIssues([]);
      return false;
    }
    designValidationActiveRef.current = true;
    setDesignIssues(issues);
    const firstId = issues.find((i) => i.fieldId)?.fieldId;
    if (firstId) {
      setSelectedId(firstId);
      window.requestAnimationFrame(() => {
        const el =
          document.querySelector(
            `.esource-builder-field-row.is-invalid`,
          ) ||
          document.querySelector(`.esource-builder-field-row.is-selected`);
        el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      });
    } else {
      window.requestAnimationFrame(() => {
        document
          .querySelector(".esource-builder-design-banner")
          ?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      });
    }
    return true;
  };

  const ensureDesignValidForSave = () => {
    const issues = validateTemplateDesign(fields, logic);
    return !reportDesignValidation(issues);
  };

  const requestPreviewOrSave = (intent) => {
    if (isReadOnly && intent !== "preview") {
      notify(
        "Published form",
        "This published form is preview-only. Create a new version from Form Management to edit.",
        "warning",
      );
      return;
    }
    if (intent !== "preview" && !ensureDesignValidForSave()) {
      return;
    }
    if (isFormIntroIncomplete()) {
      setIntroPromptIntent(intent);
      return;
    }
    if (intent === "preview") openPreview();
    else openSaveModal(intent);
  };

  const continueWithoutFormIntro = () => {
    const intent = introPromptIntent;
    setIntroPromptIntent(null);
    if (intent && intent !== "preview" && !ensureDesignValidForSave()) {
      return;
    }
    if (intent === "preview") openPreview();
    else if (intent) openSaveModal(intent);
  };

  const addFormIntroNow = () => {
    setIntroPromptIntent(null);
    focusFormIntroEditor();
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewErrors([]);
    setPreviewSubmitMsg("");
    if (forcePreview) {
      navigate("/esource/templates");
    }
  };

  const setPreviewFieldValue = (fieldId, value) => {
    setPreviewValues((prev) => ({ ...prev, [fieldId]: value }));
    setPreviewErrors((prev) => prev.filter((id) => id !== fieldId));
    setPreviewSubmitMsg("");
  };

  const onPreviewSubmit = () => {
    const { visible } = evaluateLogicVisibility(fields, previewValues, logic);
    const missing = collectRequiredMissing(fields, previewValues, visible);
    if (missing.length) {
      setPreviewErrors(missing.map((f) => f.id));
      setPreviewSubmitMsg(
        `Please complete required fields: ${missing
          .map((f) => f.label || "Untitled")
          .join(", ")}`,
      );
      const firstId = missing[0]?.id;
      if (firstId) {
        window.requestAnimationFrame(() => {
          const el =
            document.getElementById(`esource-preview-${firstId}`) ||
            document.getElementById(`esource-preview-${firstId}-label`) ||
            document.querySelector(
              `[aria-labelledby="esource-preview-${firstId}-label"]`,
            );
          el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
          if (el?.focus) el.focus();
        });
      }
      return;
    }
    const issues = collectFieldValidationIssues(
      fields,
      previewValues,
      visible,
    );
    if (issues.length) {
      setPreviewErrors(issues.map((i) => i.id));
      setPreviewSubmitMsg(issues.map((i) => i.message).join(" "));
      const firstId = issues[0]?.id;
      if (firstId) {
        window.requestAnimationFrame(() => {
          const el =
            document.getElementById(`esource-preview-${firstId}`) ||
            document.getElementById(`esource-preview-${firstId}-label`);
          el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
          if (el?.focus) el.focus();
        });
      }
      return;
    }
    setPreviewErrors([]);
    setPreviewSubmitMsg(
      "Validation passed. Required fields and field rules are complete.",
    );
    notify(
      "Preview validation",
      "Required fields and field rules are complete.",
      "success",
    );
  };

  useEffect(() => {
    if (!fieldSettingsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeFieldSettings();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fieldSettingsOpen]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [previewOpen]);

  const reorderField = (fromId, toId) => {
    if (isReadOnly) return;
    if (!fromId || !toId || fromId === toId) return;
    setFields((prev) => {
      const fromIdx = prev.findIndex((f) => f.id === fromId);
      const toIdx = prev.findIndex((f) => f.id === toId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev;
      const copy = [...prev];
      const [item] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, item);
      return copy;
    });
  };

  const onPaletteDragStart = (e, typeValue) => {
    if (isReadOnly) return;
    dragPaletteTypeRef.current = typeValue;
    dragFieldIdRef.current = null;
    e.dataTransfer.effectAllowed = "copyMove";
    e.dataTransfer.setData(DND_FIELD_TYPE, typeValue);
    e.dataTransfer.setData("text/plain", `${DND_TYPE_PREFIX}${typeValue}`);
    e.currentTarget.classList.add("is-dragging");
  };

  const onPaletteDragEnd = (e) => {
    e.currentTarget.classList.remove("is-dragging");
    clearCanvasDragState();
  };

  const onCanvasPaletteDragOver = (e) => {
    if (isReadOnly) return;
    if (!isPaletteDragActive(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!canvasPaletteOver) setCanvasPaletteOver(true);
  };

  const onCanvasPaletteDrop = (e) => {
    if (isReadOnly) return;
    if (e.target.closest?.(".esource-builder-field-row")) return;
    if (e.target.closest?.(".esource-builder-form-intro")) return;
    if (!isPaletteDragActive(e)) return;
    e.preventDefault();
    const type = readPaletteType(e.dataTransfer);
    if (type) insertFieldAt(type, null);
    clearCanvasDragState();
  };

  const closeSaveModal = () => {
    setSaveIntent(null);
  };

  const onSave = async ({
    silent = false,
    codeValue = code,
    nameValue = name,
    moduleScopeValue = moduleScope,
  } = {}) => {
    const nextCode = String(codeValue || "").trim();
    const nextName = String(nameValue || "").trim();
    if (!nextCode || !nextName) {
      notify("Validation", "Code and name are required.", "warning");
      return null;
    }
    showLoader();
    try {
      const res = await saveTemplate({
        FormTemplateId: formTemplateId || null,
        Code: nextCode,
        Name: nextName,
        ModuleScope: moduleScopeValue || OTHER_FORM_TYPE,
        SchemaJson: buildSchemaJson({
          fields,
          formHeader,
          formDescription,
          fillAccess: FILL_ACCESS_INTERNAL,
          settings: formSettings,
          logic,
        }),
      });
      if (!res.success) {
        notify(
          isNew ? "Save draft" : "Update",
          res.message || (isNew ? "Save failed." : "Update failed."),
          "danger",
        );
        return null;
      }
      setCode(nextCode);
      setName(nextName);
      setModuleScope(moduleScopeValue || OTHER_FORM_TYPE);
      if (!silent) {
        notify(
          isNew ? "Save" : "Update",
          isNew
            ? "Form has been saved successfully"
            : "Form has been updated successfully",
          "success",
        );
      }
      const newId = res.formTemplateId || formTemplateId;
      if (newId) {
        setFormTemplateId(newId);
        if (isNew) {
          navigate(`/esource/templates/${newId}`, { replace: true });
        }
      }
      return newId || null;
    } finally {
      hideLoader();
    }
  };

  const onPublish = async ({
    codeValue = code,
    nameValue = name,
    moduleScopeValue = moduleScope,
  } = {}) => {
    const templateId = await onSave({
      silent: true,
      codeValue,
      nameValue,
      moduleScopeValue,
    });
    if (!templateId) return;
    const res = await confirmAndPublishTemplate(templateId, {
      showAlert,
      draftVersionNo,
      publishedVersionNo,
      formName: nameValue || name,
      formCode: codeValue || code,
    });
    if (res.cancelled) return;
    if (!res.success) {
      notify(
        "Publish failed",
        res.message || "Publish failed.",
        "danger",
      );
      return;
    }
    showAlert({
      title: "Published successfully",
      message: "Form has been published successfully",
      type: "success",
      buttonText: "OK",
      onConfirm: () => {
        navigate(`/esource/templates/${templateId}?mode=preview`, {
          replace: true,
        });
      },
    });
  };

  const confirmSaveModal = async () => {
    if (!ensureDesignValidForSave()) {
      closeSaveModal();
      return;
    }
    const intent = saveIntent;
    const payload = {
      codeValue: saveCode,
      nameValue: saveName,
      moduleScopeValue: saveModuleScope,
    };
    if (!String(saveCode || "").trim() || !String(saveName || "").trim()) {
      notify("Validation", "Code and name are required.", "warning");
      return;
    }
    closeSaveModal();
    if (intent === "publish") {
      await onPublish(payload);
    } else {
      await onSave(payload);
    }
  };

  const clearFormCanvas = () => {
    if (isReadOnly) return;
    if (!fields.length && !formHeader && !formDescription && !logic?.length) {
      return;
    }
    showAlert({
      title: "Clear canvas?",
      message:
        "All fields, form settings content, and conditional logic on this draft will be removed. This cannot be undone until you save again.",
      type: "warning",
      buttonText: "Clear",
      cancelText: "Cancel",
      onConfirm: () => {
        setFields([]);
        setSelectedId(null);
        setFieldSettingsOpen(false);
        setLogic([]);
        setFormHeader("");
        setFormDescription("");
        setFormSettings(normalizeFormSettings({ layout: "two-column" }));
        setSelectedLibraryId(null);
        setAssistMessages([]);
        setAssistSessionId(newFormAssistSessionId());
        setAiPrompt("");
        setDesignIssues([]);
        designValidationActiveRef.current = false;
        clearCanvasDragState();
      },
    });
  };

  const assistOrgId =
    userData?.OrganizationId || userData?.Organizationid || "";
  const assistStaffId = userData?.StaffId || "";

  const refreshAssistSessions = useCallback(() => {
    setAssistRecentSessions(
      readFormAssistSessions(assistOrgId, assistStaffId),
    );
  }, [assistOrgId, assistStaffId]);

  useEffect(() => {
    if (leftTool !== "assist") return;
    refreshAssistSessions();
  }, [leftTool, refreshAssistSessions]);

  const assistUsage = useMemo(() => {
    const cap = Math.max(1, Number(assistStatus?.dailyUserCap) || 50);
    const used = Math.max(0, Number(assistStatus?.userUsedToday) || 0);
    return { used, cap };
  }, [assistStatus]);

  const mergeAssistQuota = useCallback((partial) => {
    if (!partial) return;
    setAssistStatus((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        dailyOrgCap: partial.dailyOrgCap ?? prev.dailyOrgCap,
        dailyUserCap: partial.dailyUserCap ?? prev.dailyUserCap,
        orgUsedToday: partial.orgUsedToday ?? prev.orgUsedToday,
        userUsedToday: partial.userUsedToday ?? prev.userUsedToday,
        orgRemaining: partial.orgRemaining ?? prev.orgRemaining,
        userRemaining: partial.userRemaining ?? prev.userRemaining,
      };
    });
  }, []);

  const formBuilderTourSteps = useMemo(
    () =>
      buildEsourceFormBuilderTourSteps({
        isNew,
        isReadOnly,
        hasFields: fields.length > 0,
        setLeftTool,
      }),
    [isNew, isReadOnly, fields.length],
  );

  useEffect(() => {
    if (loading || !canDesign || forcePreview || !assistStaffId) {
      return undefined;
    }
    if (hasCompletedEsourceFormBuilderTour(assistStaffId)) return undefined;
    const timer = window.setTimeout(() => setShowFormBuilderTour(true), 1000);
    return () => window.clearTimeout(timer);
  }, [loading, canDesign, forcePreview, assistStaffId]);

  const startAssistNewChat = useCallback(() => {
    setAssistSessionId(newFormAssistSessionId());
    setAssistMessages([]);
    setAiPrompt("");
  }, []);

  const openAssistSession = useCallback(
    (session) => {
      const id = String(session?.id || "").trim();
      if (!id) return;
      const detail =
        getFormAssistSession(assistOrgId, assistStaffId, id) || session;
      setAssistSessionId(id);
      setAssistMessages(Array.isArray(detail.messages) ? detail.messages : []);
      setAiPrompt("");
    },
    [assistOrgId, assistStaffId],
  );

  const applyGeneratedForm = (result, { applyToCanvas = true } = {}) => {
    if (result?.blocked) {
      return result;
    }
    if (!applyToCanvas || result?.replaceCanvas === false) {
      return result;
    }
    if (result.fields?.length) {
      setFields(result.fields);
      const focusId = result.incremental
        ? result.fields[result.fields.length - 1]?.id
        : result.fields[0]?.id;
      setSelectedId(focusId || null);
      if (!result.incremental) {
        setLogic([]);
        setFormSettings(normalizeFormSettings(null));
      }
    }
    if (result.code && (isNew || !code)) setCode(result.code);
    if (result.name && (isNew || !name)) setName(result.name);
    if (result.moduleScope) {
      setModuleScope(mapLegacyScopeToFormType(result.moduleScope));
    }
    return result;
  };

  const openAqAssist = useCallback(async () => {
    const fallbackTool = isNew ? "library" : "details";
    const organizationId =
      userData?.OrganizationId || userData?.Organizationid || "";
    const staffId = userData?.StaffId || "";
    showLoader();
    try {
      const status = await fetchFaqAiStatus(organizationId, staffId);
      if (!status?.formBuilderEnabled) {
        setAssistUnavailableMessage(
          "AQ Assist for Form Builder is not enabled for your organisation yet. Ask your Super Admin to turn on AQ Assist for Form Builder.",
        );
        setAssistUnavailableOpen(true);
        setLeftTool(fallbackTool);
        setAssistStatus(null);
        return;
      }
      setAssistStatus(status);
      setLeftTool("assist");
    } catch (err) {
      setAssistUnavailableMessage(
        "AQ Assist is not available right now. You can request access below.",
      );
      setAssistUnavailableOpen(true);
      setLeftTool(fallbackTool);
      setAssistStatus(null);
    } finally {
      hideLoader();
    }
  }, [isNew, userData, showLoader, hideLoader]);

  const handleAssistSupportSubmit = useCallback(
    async (requestNote) => {
      const note = String(requestNote || "").trim();
      if (!note || assistSupportSubmitting) return;
      setAssistSupportSubmitting(true);
      try {
        const orgName =
          userData?.OrganizationName ||
          userData?.OrgName ||
          userData?.organisationName ||
          "";
        const result = await submitFeedbackToClickUp({
          inquiryType: "Requesting a New Feature",
          module: "e-Source",
          taskName: "Support: Enable AQ Assist for Form Builder",
          description: [
            note,
            "",
            "---",
            "Context: User opened Form Builder → AQ Assist and Form Builder AQ Assist is not enabled for this organisation.",
            orgName ? `Organisation: ${orgName}` : null,
          ]
            .filter(Boolean)
            .join("\n"),
        });
        if (result.success) {
          notify(
            "Support ticket created",
            "Your request has been sent to the AQ team. We will get back to you shortly.",
            "success",
          );
          setAssistUnavailableOpen(false);
        } else {
          notify(
            "Unable to create ticket",
            result.message || "Please try again or contact support.",
            "danger",
          );
        }
      } catch (err) {
        notify(
          "Unable to create ticket",
          "Please try again or contact support.",
          "danger",
        );
      } finally {
        setAssistSupportSubmitting(false);
      }
    },
    [assistSupportSubmitting, notify, userData],
  );

  const runAiAssist = async (promptOverride, options = {}) => {
    if (isReadOnly) {
      notify(
        "Published form",
        "This published form is preview-only. Create a new version from Form Management to edit.",
        "warning",
      );
      return;
    }
    if (!assistStatus?.formBuilderEnabled) {
      await openAqAssist();
      return;
    }
    const prompt = String(promptOverride ?? aiPrompt).trim();
    if (!prompt) {
      setAssistMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          text: "Describe the form you want to build.",
          error: true,
        },
      ]);
      return;
    }
    if (
      (assistStatus.orgRemaining != null && assistStatus.orgRemaining <= 0) ||
      (assistStatus.userRemaining != null && assistStatus.userRemaining <= 0)
    ) {
      setAssistMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          text: "You have reached today’s AQ Assist limit. Try again tomorrow.",
          error: true,
        },
      ]);
      return;
    }
    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      text: String(options.userDisplayText || prompt).trim() || prompt,
    };
    setAssistMessages((prev) => [...prev, userMsg]);
    setAiPrompt("");
    setAiBusy(true);
    try {
      setSelectedLibraryId(null);
      const allowedModes =
        Array.isArray(assistStatus.formBuilderModes) &&
        assistStatus.formBuilderModes.length
          ? assistStatus.formBuilderModes
          : ["clinicalPacks"];

      let serverResult = null;
      const forceLocalDocument = !!options.forceLocalDocument;
      const apiQuestion = forceLocalDocument
        ? String(
            options.userDisplayText ||
              "Import e-Source form from uploaded document.",
          ).slice(0, 480)
        : prompt;
      try {
        serverResult = await askFormBuilderAssist({
          organizationId: assistOrgId,
          staffId: assistStaffId,
          question: apiQuestion,
          chatSessionId: assistSessionId,
          formName: name,
          formHeader,
          canvasSnapshot: buildCanvasSnapshotForAssist(fields),
          allowedModes,
        });
        mergeAssistQuota(serverResult);
      } catch (askErr) {
        const msg = String(askErr?.message || "").trim();
        if (!forceLocalDocument) {
          setAssistMessages((prev) => {
            const next = [
              ...prev,
              {
                id: `a-${Date.now()}`,
                role: "assistant",
                text: msg || "Could not reach AQ Assist. Please try again.",
                error: true,
              },
            ];
            setAssistRecentSessions(
              upsertFormAssistSession({
                organizationId: assistOrgId,
                staffId: assistStaffId,
                sessionId: assistSessionId,
                messages: next,
              }),
            );
            return next;
          });
          try {
            const status = await fetchFaqAiStatus(assistOrgId, assistStaffId);
            if (status?.formBuilderEnabled) setAssistStatus(status);
          } catch {
            /* keep prior status */
          }
          return;
        }
      }

      const localAssistOpts = {
        allowedModes,
        currentFields: fields,
        formName: name,
        formHeader,
        recentMessages: [...assistMessages, userMsg],
      };

      const beforeFields = fields;
      let result;
      if (forceLocalDocument) {
        // Full document text stays on the client — avoids FAQ 1000-char gate.
        result = applyGeneratedForm(
          generateESourceFormFromPrompt(prompt, localAssistOpts),
        );
      } else if (serverResult?.useLocal) {
        result = applyGeneratedForm(
          generateESourceFormFromPrompt(prompt, localAssistOpts),
        );
      } else {
        const serverApplied = applyFormBuilderServerAssist(
          serverResult,
          fields,
        );
        const serverTouched =
          !serverApplied?.blocked &&
          serverApplied?.replaceCanvas !== false &&
          Array.isArray(serverApplied?.fields) &&
          serverApplied.fields.length > 0 &&
          assistCanvasDidChange(beforeFields, serverApplied.fields);
        if (!serverTouched && !serverResult?.blocked) {
          const localApplied = generateESourceFormFromPrompt(
            prompt,
            localAssistOpts,
          );
          const localTouched =
            !localApplied?.blocked &&
            localApplied?.replaceCanvas !== false &&
            Array.isArray(localApplied?.fields) &&
            localApplied.fields.length > 0 &&
            assistCanvasDidChange(beforeFields, localApplied.fields);
          result = applyGeneratedForm(
            localTouched
              ? {
                  ...localApplied,
                  summary:
                    localApplied.summary ||
                    serverApplied?.summary ||
                    serverResult?.summary,
                }
              : serverApplied,
          );
        } else {
          result = applyGeneratedForm(serverApplied);
        }
      }

      const afterFields =
        result?.replaceCanvas !== false &&
        !result?.blocked &&
        Array.isArray(result?.fields) &&
        result.fields.length > 0
          ? result.fields
          : beforeFields;
      const applied = assistCanvasDidChange(beforeFields, afterFields);
      const changeNotes = applied
        ? summarizeAssistFieldChanges(beforeFields, afterFields)
        : [];
      const wantedEdit = forceLocalDocument
        ? true
        : looksLikeAssistEditIntent(prompt);
      let assistantText =
        result?.summary ||
        serverResult?.summary ||
        (result?.blocked
          ? "That request is outside the forms allowed for your organisation."
          : applied
            ? "Fields updated."
            : "No changes were applied to the canvas.");
      if (!applied && !result?.blocked && wantedEdit) {
        assistantText = [
          assistantText,
          "",
          "No changes were applied to the canvas. Name fields as they appear on the canvas (or say “field 7”), then try again.",
        ].join("\n");
      }
      const assistantMsg = {
        id: `a-${Date.now()}`,
        role: "assistant",
        text: assistantText,
        blocked: Boolean(result?.blocked),
        error: Boolean(result?.blocked) || (wantedEdit && !applied),
        applied,
        fieldLabels: changeNotes,
      };
      setAssistMessages((prev) => {
        const next = [...prev, assistantMsg];
        setAssistRecentSessions(
          upsertFormAssistSession({
            organizationId: assistOrgId,
            staffId: assistStaffId,
            sessionId: assistSessionId,
            messages: next,
          }),
        );
        return next;
      });
    } catch (err) {
      setAssistMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          text: err?.message || "Could not generate fields. Please try again.",
          error: true,
        },
      ]);
    } finally {
      setAiBusy(false);
    }
  };

  const handleAssistComposerSend = async (payload = {}) => {
    const text =
      typeof payload === "string"
        ? payload
        : String(payload?.text || "").trim();
    const file = typeof payload === "object" ? payload?.file : null;

    if (file) {
      if (isReadOnly) {
        notify(
          "Published form",
          "This published form is preview-only. Create a new version from Form Management to edit.",
          "warning",
        );
        return;
      }
      if (!assistStatus?.formBuilderEnabled) {
        await openAqAssist();
        return;
      }
      try {
        const extracted = await extractAssistDocumentText(file);
        const prompt = buildFormFromDocumentPrompt({
          ...extracted,
          userNote: text,
        });
        await runAiAssist(prompt, {
          userDisplayText: text
            ? `Uploaded “${extracted.fileName}”: ${text}`
            : `Uploaded “${extracted.fileName}” — generate a form from this document.`,
          forceLocalDocument: true,
        });
      } catch (err) {
        setAssistMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            text:
              err?.message ||
              "Could not read that file. Please upload a PDF or Word document (.pdf, .doc, .docx).",
            error: true,
          },
        ]);
      }
      return;
    }

    await runAiAssist(text || undefined);
  };

  const applyLibraryTemplate = (templateId) => {
    if (isReadOnly) return;
    setSelectedLibraryId(String(templateId));
    applyGeneratedForm(applyFormLibraryTemplate(templateId));
  };

  const applyVersionSchemaToPreview = useCallback((version) => {
    if (!version) return;
    const parsed = parseSchemaJson(version.SchemaJson);
    setFields(parsed.fields);
    setFormHeader(parsed.formHeader || "");
    setFormDescription(parsed.formDescription || "");
    setFormSettings(normalizeFormSettings(parsed.settings));
    setLogic(Array.isArray(parsed.logic) ? parsed.logic : []);
    setPreviewVersionId(versionRowId(version));
    setPreviewValues(applyFieldDefaults(parsed.fields, {}));
    setPreviewErrors([]);
    setPreviewSubmitMsg("");
  }, []);

  const previewVersionOptions = useMemo(
    () =>
      templateVersions.map((version) => ({
        value: versionRowId(version),
        label: formatVersionOptionLabel(version),
      })),
    [templateVersions],
  );

  const selectedPreviewVersionOption = useMemo(() => {
    if (!previewVersionId) return null;
    return (
      previewVersionOptions.find((opt) => opt.value === previewVersionId) ||
      null
    );
  }, [previewVersionId, previewVersionOptions]);

  const onPreviewVersionChange = useCallback(
    (option) => {
      const nextId = String(option?.value || "").trim();
      if (!nextId || nextId === previewVersionId) return;
      const version = templateVersions.find(
        (row) => versionRowId(row) === nextId,
      );
      if (!version) return;
      applyVersionSchemaToPreview(version);
    },
    [
      previewVersionId,
      templateVersions,
      applyVersionSchemaToPreview,
    ],
  );

  const goToBuilderEdit = useCallback(() => {
    const templateId = formTemplateId || id;
    if (!templateId) return;
    navigate(`/esource/templates/${templateId}`);
  }, [formTemplateId, id, navigate]);

  const handleEditFromPreview = useCallback(() => {
    const templateId = formTemplateId || id;
    if (!templateId) return;

    if (draftVersionNo != null) {
      setPreviewOpen(false);
      goToBuilderEdit();
      return;
    }

    showAlert({
      title: "Create draft version?",
      message:
        "A new draft will be created from the latest published form so you can edit it. The published version stays unchanged until you publish again.",
      type: "warning",
      buttonText: "Create draft & edit",
      cancelText: "Cancel",
      onConfirm: async () => {
        showLoader();
        try {
          const res = await createDraftVersion(templateId);
          if (!res.success) {
            notify(
              "Create draft version",
              res.message || "Could not create a draft.",
              "danger",
            );
            return;
          }
          setPreviewOpen(false);
          navigate(`/esource/templates/${templateId}`);
        } finally {
          hideLoader();
        }
      },
    });
  }, [
    formTemplateId,
    id,
    draftVersionNo,
    goToBuilderEdit,
    showAlert,
    showLoader,
    hideLoader,
    notify,
    navigate,
  ]);

  const previewModal = (
    <CtmsWorkspaceModal
      open={previewOpen}
      title={
        name?.trim() || code?.trim()
          ? `Form preview — ${name?.trim() || code}`
          : "Form preview"
      }
      titleExtras={
        <>
          {moduleScope ? (
            <span className="esource-builder-preview-modal__type-badge">
              {moduleScope}
            </span>
          ) : null}
          {forcePreview && previewVersionOptions.length > 1 ? (
            <div className="esource-builder-preview-modal__version-select">
              <span className="esource-builder-preview-modal__version-label">
                Version
              </span>
              <Dropdown
                options={previewVersionOptions}
                selected={selectedPreviewVersionOption}
                onSelect={onPreviewVersionChange}
                showSearch={false}
                size="sm"
              />
            </div>
          ) : selectedPreviewVersionOption && forcePreview ? (
            <span className="esource-builder-preview-modal__type-badge esource-builder-preview-modal__type-badge--version">
              {selectedPreviewVersionOption.label}
            </span>
          ) : draftVersionNo != null ? (
            <span className="esource-builder-preview-modal__type-badge esource-builder-preview-modal__type-badge--version">
              Draft v{draftVersionNo}
            </span>
          ) : publishedVersionNo != null ? (
            <span className="esource-builder-preview-modal__type-badge esource-builder-preview-modal__type-badge--version">
              v{publishedVersionNo}
            </span>
          ) : null}
        </>
      }
      headerActions={
        forcePreview || isReadOnly ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleEditFromPreview}
            title={
              draftVersionNo != null
                ? "Edit draft in Form Builder"
                : "Create a draft and open Form Builder"
            }
          >
            <i className="fas fa-pen me-1" aria-hidden="true" />
            Edit
          </button>
        ) : null
      }
      titleId="esource-preview-modal-title"
      onClose={closePreview}
      className="esource-builder-preview-modal"
      dialogClassName="ctms-workspace-modal__dialog--full"
      contentClassName="esource-builder-preview-modal__content"
    >
      <div className="esource-builder-preview-modal__form">
        {formHeader || formDescription ? (
          <div className="esource-form-intro-block">
            {formHeader ? (
              <h2 className="esource-form-intro-block__header">{formHeader}</h2>
            ) : null}
            {formDescription ? (
              <p className="esource-form-intro-block__description">
                {formDescription}
              </p>
            ) : null}
          </div>
        ) : null}
        {previewSubmitMsg ? (
          <div
            className={`esource-builder-preview-modal__alert${
              previewErrors.length
                ? " esource-builder-preview-modal__alert--danger"
                : " esource-builder-preview-modal__alert--success"
            }`}
            role="status"
          >
            {previewSubmitMsg}
          </div>
        ) : null}
        <ESourceFormFields
          fields={fields}
          values={previewValues}
          onChange={setPreviewFieldValue}
          idPrefix="esource-preview-"
          invalidIds={previewErrors}
          emptyText={
            forcePreview
              ? "This form has no fields to preview."
              : "Add fields on the canvas to preview this form."
          }
          logic={logic}
          layout={formSettings?.layout || "two-column"}
        />
        {fields.length > 0 ? (
          <div className="esource-builder-preview-modal__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onPreviewSubmit}
            >
              Submit
            </button>
          </div>
        ) : null}
      </div>
    </CtmsWorkspaceModal>
  );

  // Form Management name-click: same full-width preview modal as Form Builder.
  if (forcePreview && !isNew) {
    return (
      <ESourceAccessGate loading={loading} allow={canDesign}>
        <div className="esource-page esource-form-preview-host" aria-hidden="true" />
        {previewModal}
        <InfoAlert {...alertState} onClose={hideAlert} />
      </ESourceAccessGate>
    );
  }

  return (
    <ESourceAccessGate loading={loading} allow={canDesign}>
      <div className="esource-page esource-builder-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "Form Management", path: "/esource/templates" },
            { label: "Form Builder" },
          ]}
          trailingExtras={
            <div className="esource-builder-crumb-actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm esource-tour-help-btn"
                data-tour="esource-fb-guide"
                onClick={() => setShowFormBuilderTour(true)}
                title="Show page guide"
              >
                <i className="fas fa-route me-1" aria-hidden="true" />
                Guide
              </button>
              {isReadOnly ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("/esource/templates")}
                  title="Back to Form Management"
                >
                  <i className="fas fa-arrow-left me-1" aria-hidden="true" />
                  Form Management
                </button>
              ) : isNew ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearFormCanvas}
                  disabled={
                    fields.length === 0 &&
                    !formHeader &&
                    !formDescription &&
                    !(logic || []).length
                  }
                  title="Clear canvas"
                >
                  <i className="fas fa-eraser me-1" aria-hidden="true" />
                  Clear
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("/esource/templates/new")}
                  title="Create a new form"
                >
                  <i className="fas fa-plus me-1" aria-hidden="true" />
                  Add new
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                data-tour="esource-fb-preview"
                onClick={() => requestPreviewOrSave("preview")}
                disabled={fields.length === 0}
                title="Preview form"
              >
                <i className="fas fa-eye me-1" aria-hidden="true" />
                Preview
              </button>
              {!isReadOnly ? (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  data-tour="esource-fb-save"
                  onClick={() => requestPreviewOrSave("draft")}
                >
                  <i className="fas fa-save me-1" aria-hidden="true" />
                  {isNew ? "Save draft" : "Update"}
                </button>
              ) : null}
            </div>
          }
        />

        <div
          className={`esource-builder-layout${
            isNew ? "" : " esource-builder-layout--editing"
          }`}
        >
          <aside className="esource-builder-library-panel">
            {isNew ? (
              <>
              <div
                className="esource-builder-tool-switch"
                role="tablist"
                aria-label="Form starter"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftTool === "library"}
                  className={`esource-builder-tool-switch__btn${
                    leftTool === "library" ? " is-active" : ""
                  }`}
                  data-tour="esource-fb-proto-tab"
                  onClick={() => setLeftTool("library")}
                >
                  Proto Types
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftTool === "assist"}
                  className={`esource-builder-tool-switch__btn esource-builder-tool-switch__btn--assist${
                    leftTool === "assist" ? " is-active" : ""
                  }`}
                  data-tour="esource-fb-assist-tab"
                  onClick={() => {
                    void openAqAssist();
                  }}
                  title="AQ Assist"
                  aria-label="Build with AQ Assist"
                >
                  <FaqAssistMark />
                  AQ Assist
                  <FaqAssistAiIcon className="esource-builder-aq-assist__ai" />
                </button>
              </div>

              <div className="esource-builder-rail-top">
                {leftTool === "library" ? (
                  <div className="esource-builder-library">
                    <div className="esource-builder-library__intro">
                      <div className="esource-builder-library__filter">
                        <label className="esource-builder-library__filter-label">
                          Form type
                        </label>
                        <div className="esource-builder-dd--mid">
                          <Dropdown
                            options={libraryCategoryOptions}
                            isMulti={false}
                            showSearch
                            selected={
                              libraryCategoryOptions.find(
                                (o) => o.value === libraryCategory,
                              ) || libraryCategoryOptions[0]
                            }
                            onSelect={(opt) =>
                              setLibraryCategory(
                                opt?.value || libraryCategories[0] || "",
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="esource-builder-readymade">
                      {filteredLibrary.map((pack) => {
                        const isSelected =
                          String(selectedLibraryId) ===
                          String(pack.templateId);
                        return (
                        <button
                          key={pack.templateId}
                          type="button"
                          ref={isSelected ? selectedLibraryCardRef : null}
                          className={`esource-builder-readymade__item${
                            isSelected ? " is-selected" : ""
                          }`}
                          aria-pressed={isSelected}
                          onClick={() => applyLibraryTemplate(pack.templateId)}
                        >
                          <span className="esource-builder-readymade__top">
                            <span className="esource-builder-readymade__code">
                              {pack.formCode || pack.templateId}
                            </span>
                            <span className="esource-builder-readymade__foot-inline">
                              {pack.fieldCount} fields
                            </span>
                          </span>
                          <span
                            className="esource-builder-readymade__name"
                            title={pack.templateName}
                          >
                            {pack.templateName}
                          </span>
                          {pack.description ? (
                            <span
                              className="esource-builder-readymade__desc"
                              title={pack.description}
                            >
                              {pack.description}
                            </span>
                          ) : null}
                        </button>
                        );
                      })}
                      {filteredLibrary.length === 0 ? (
                        <p className="esource-builder-empty-text mb-0">
                          No proto types for this form type.
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <ESourceFormAssistPane
                    mode="create"
                    draft={aiPrompt}
                    onDraftChange={setAiPrompt}
                    messages={assistMessages}
                    busy={aiBusy}
                    hasCanvasFields={fields.length > 0}
                    recentSessions={assistRecentSessions}
                    activeSessionId={assistSessionId}
                    usage={assistUsage}
                    onSend={(payload) => {
                      void handleAssistComposerSend(payload);
                    }}
                    onNewChat={startAssistNewChat}
                    onOpenSession={openAssistSession}
                  />
                )}
              </div>
              </>
            ) : (
              <>
                {!isReadOnly ? (
                <div
                  className="esource-builder-tool-switch"
                  role="tablist"
                  aria-label="Form editor tools"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={leftTool !== "assist"}
                    className={`esource-builder-tool-switch__btn${
                      leftTool !== "assist" ? " is-active" : ""
                    }`}
                    data-tour="esource-fb-details-tab"
                    onClick={() => setLeftTool("details")}
                  >
                    Form details
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={leftTool === "assist"}
                    className={`esource-builder-tool-switch__btn esource-builder-tool-switch__btn--assist${
                      leftTool === "assist" ? " is-active" : ""
                    }`}
                    data-tour="esource-fb-assist-tab"
                    onClick={() => {
                      void openAqAssist();
                    }}
                    title="AQ Assist"
                    aria-label="Change this form with AQ Assist"
                  >
                    <FaqAssistMark />
                    AQ Assist
                    <FaqAssistAiIcon className="esource-builder-aq-assist__ai" />
                  </button>
                </div>
                ) : (
                  <h6 className="esource-builder-form-summary__title mb-0">
                    Published form
                  </h6>
                )}

                <div className="esource-builder-rail-top">
                  {!isReadOnly && leftTool === "assist" ? (
                    <ESourceFormAssistPane
                      mode="edit"
                      draft={aiPrompt}
                      onDraftChange={setAiPrompt}
                      messages={assistMessages}
                      busy={aiBusy}
                      hasCanvasFields={fields.length > 0}
                      recentSessions={assistRecentSessions}
                      activeSessionId={assistSessionId}
                      usage={assistUsage}
                      onSend={(payload) => {
                        void handleAssistComposerSend(payload);
                      }}
                      onNewChat={startAssistNewChat}
                      onOpenSession={openAssistSession}
                    />
                  ) : (
                    <div className="esource-builder-form-summary">
                      <p className="esource-builder-form-summary__hint">
                        {isReadOnly
                          ? "Published form — preview only. Create a new version from Form Management to edit."
                          : "You opened this from Form Management. Edit on the canvas, or use AQ Assist to change fields. Starter prototypes stay hidden while editing."}
                      </p>
                      <dl className="esource-builder-form-summary__meta">
                        <div>
                          <dt>Code</dt>
                          <dd>{code || "—"}</dd>
                        </div>
                        <div>
                          <dt>Name</dt>
                          <dd>{name || "—"}</dd>
                        </div>
                        <div>
                          <dt>Scope</dt>
                          <dd>{moduleScope || "—"}</dd>
                        </div>
                        <div>
                          <dt>Draft</dt>
                          <dd>
                            {draftVersionNo != null
                              ? `v${draftVersionNo}`
                              : "—"}
                          </dd>
                        </div>
                        <div>
                          <dt>Published</dt>
                          <dd>
                            {publishedVersionNo != null
                              ? `v${publishedVersionNo}`
                              : "—"}
                          </dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary w-100"
                        onClick={() => navigate("/esource/templates")}
                      >
                        Back to Form Management
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </aside>

          <div className="esource-builder-workspace">
            <div className="esource-builder-workspace__grid esource-builder-workspace__grid--main">
            <section
              className="esource-builder-col esource-builder-col--canvas"
              data-tour="esource-fb-canvas"
            >
              <div className="esource-builder-col__head esource-builder-col__head--row">
                <span className="esource-builder-col__title">Form canvas</span>
                <div className="esource-builder-col__head-actions">
                  {!isReadOnly ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      data-tour="esource-fb-form-settings"
                      onClick={() => setFormSettingsOpen(true)}
                    >
                      Form settings
                    </button>
                  ) : (
                    <span className="esource-builder-readonly-badge">
                      Preview only
                    </span>
                  )}
                  <span className="esource-builder-col__count">
                    {fields.length} field{fields.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              {isReadOnly ? (
                <div className="esource-builder-design-banner" role="status">
                  <div className="esource-builder-design-banner__item">
                    This is the published form. Create a new version from Form
                    Management to make changes.
                  </div>
                </div>
              ) : null}
              <div className="esource-builder-canvas-split">
              <div
                className={`esource-builder-canvas-body${
                  canvasPaletteOver ? " is-palette-drag-over" : ""
                }`}
                onDragOver={onCanvasPaletteDragOver}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setCanvasPaletteOver(false);
                }}
                onDrop={onCanvasPaletteDrop}
              >
                {formLevelDesignErrors.length ? (
                  <div
                    className="esource-builder-design-banner"
                    role="alert"
                  >
                    {formLevelDesignErrors.map((msg) => (
                      <div key={msg} className="esource-builder-design-banner__item">
                        {msg}
                      </div>
                    ))}
                  </div>
                ) : null}

                {fields.length === 0 ? (
                  <div
                    className={`esource-builder-empty${
                      canvasPaletteOver ? " is-drag-over" : ""
                    }`}
                  >
                    <i
                      className="fas fa-wand-magic-sparkles"
                      aria-hidden="true"
                    />
                    <p>
                      {isNew
                        ? "Pick a starter proto type, use AQ Assist (chat or PDF/Word +), or drag field types from the right onto this canvas. Open Guide for a short walkthrough."
                        : "Use AQ Assist (chat or PDF/Word +), or drag field types from the right onto this canvas. Open Guide for a short walkthrough."}
                    </p>
                  </div>
                ) : (
                  <ul className="esource-builder-fields">
                    {fields.map((field, index) => {
                      const meta = typeMeta(field.type);
                      const active = field.id === selectedId;
                      const layout = isLayoutField(field.type);
                      const width = layout
                        ? "full"
                        : field.width || "half";
                      const isDragOver = dragOverFieldId === field.id;
                      const isEditing =
                        fieldSettingsOpen && field.id === selectedId;
                      const fieldErrors =
                        designErrorsByField.get(field.id) || [];
                      const hasDesignError = fieldErrors.length > 0;
                      return (
                        <li
                          key={field.id}
                          className={`esource-builder-field-row esource-builder-field-row--${width}${
                            active ? " is-selected" : ""
                          }${isEditing ? " is-editing" : ""}${
                            layout ? " is-layout" : ""
                          }${isDragOver ? " is-drag-over" : ""}${
                            hasDesignError ? " is-invalid" : ""
                          }`}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const palette = isPaletteDragActive(e);
                            e.dataTransfer.dropEffect = palette
                              ? "copy"
                              : "move";
                            if (dragOverFieldId !== field.id) {
                              setDragOverFieldId(field.id);
                            }
                            if (palette) setCanvasPaletteOver(false);
                          }}
                          onDragLeave={(e) => {
                            if (e.currentTarget.contains(e.relatedTarget)) {
                              return;
                            }
                            setDragOverFieldId((cur) =>
                              cur === field.id ? null : cur,
                            );
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const paletteType = readPaletteType(
                              e.dataTransfer,
                            );
                            if (paletteType) {
                              insertFieldAt(paletteType, field.id);
                              clearCanvasDragState();
                              return;
                            }
                            const fromId =
                              dragFieldIdRef.current ||
                              e.dataTransfer.getData("text/plain");
                            if (
                              fromId &&
                              !String(fromId).startsWith(DND_TYPE_PREFIX)
                            ) {
                              reorderField(fromId, field.id);
                            }
                            clearCanvasDragState();
                          }}
                        >
                          {!isReadOnly ? (
                          <span
                            className="esource-builder-field-row__grip"
                            title="Drag to reorder"
                            aria-label="Drag to reorder"
                            role="button"
                            tabIndex={0}
                            draggable
                            onDragStart={(e) => {
                              dragFieldIdRef.current = field.id;
                              dragPaletteTypeRef.current = null;
                              e.dataTransfer.effectAllowed = "move";
                              e.dataTransfer.setData("text/plain", field.id);
                              const row = e.currentTarget.closest(
                                ".esource-builder-field-row",
                              );
                              if (row) row.classList.add("is-dragging");
                            }}
                            onDragEnd={(e) => {
                              clearCanvasDragState();
                              const row = e.currentTarget.closest(
                                ".esource-builder-field-row",
                              );
                              if (row) row.classList.remove("is-dragging");
                            }}
                          >
                            <i className="fas fa-grip-vertical" aria-hidden="true" />
                          </span>
                          ) : null}
                          <button
                            type="button"
                            className="esource-builder-field-row__main"
                            onClick={() => selectField(field.id)}
                          >
                            <span className="esource-builder-field-row__index">
                              {index + 1}
                            </span>
                            <span className="esource-builder-field-row__text">
                              <span
                                className="esource-builder-field-row__label"
                                title={field.label || "Untitled"}
                              >
                                <span className="esource-builder-field-row__label-text">
                                  {field.label || "Untitled"}
                                </span>
                                {field.required ? (
                                  <span className="mandatory"> *</span>
                                ) : null}
                              </span>
                              <span className="esource-builder-field-row__type">
                                {meta.label}
                              </span>
                            </span>
                          </button>
                          {!isReadOnly ? (
                          <div className="esource-builder-field-row__ops">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              title="Settings"
                              aria-label="Field settings"
                              onClick={() => openFieldSettings(field.id)}
                            >
                              <i className="fas fa-cog" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              title="Duplicate"
                              aria-label="Duplicate field"
                              onClick={() => duplicateField(field.id)}
                            >
                              <i className="fas fa-copy" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Remove"
                              onClick={() => removeField(field.id)}
                            >
                              <i
                                className="fas fa-trash-alt"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                          ) : null}
                          {hasDesignError ? (
                            <div
                              className="esource-builder-field-row__errors"
                              role="alert"
                            >
                              {fieldErrors.map((msg) => (
                                <div
                                  key={msg}
                                  className="esource-builder-field-row__error"
                                >
                                  {msg}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              </div>
            </section>

            {!isReadOnly ? (
            <aside
              className="esource-builder-col esource-builder-col--side"
              data-tour="esource-fb-field-types"
            >
              <div className="esource-builder-side-block esource-builder-side-block--types">
                <div className="esource-builder-col__head">
                  <span className="esource-builder-col__title">
                    Field types
                  </span>
                  <span className="esource-builder-col__hint">
                    Drag or click to add
                  </span>
                </div>
                <div className="esource-builder-rail esource-builder-rail--side">
                  {FIELD_TYPE_GROUPS.map((group) => (
                    <div key={group.id} className="esource-builder-rail__group">
                      <div className="esource-builder-rail__group-label">
                        {group.label}
                      </div>
                      {group.types.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          className="esource-builder-rail__item"
                          title={`Drag onto canvas or click to add ${t.label}`}
                          draggable
                          onDragStart={(e) => onPaletteDragStart(e, t.value)}
                          onDragEnd={onPaletteDragEnd}
                          onClick={() => addField(t.value)}
                        >
                          <i className={t.icon} aria-hidden="true" />
                          <span>{t.label}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            ) : null}
          </div>
          </div>
        </div>

        {fieldSettingsOpen && selected && !isReadOnly ? (
          <div
            className="modal fade show d-block esource-builder-settings-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="esource-field-settings-title"
            style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeFieldSettings();
            }}
          >
            <div className="modal-dialog modal-xl esource-builder-settings-modal__dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="esource-builder-settings-modal__title-wrap">
                    <h5
                      className="modal-title"
                      id="esource-field-settings-title"
                    >
                      Field settings
                    </h5>
                    <span className="esource-builder-settings-modal__type">
                      {typeMeta(selected.type).label}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeFieldSettings}
                  />
                </div>
                <div className="modal-body">
                  <div className="esource-builder-settings-modal__grid">
                    <section className="esource-builder-settings-modal__col">
                      <div className="esource-builder-settings-modal__col-top">
                        <div className="esource-builder-settings-modal__preview">
                          <div className="esource-builder-settings-modal__canvas-field">
                            <span className="esource-builder-settings-modal__canvas-index">
                              {Math.max(
                                1,
                                fields.findIndex((f) => f.id === selected.id) +
                                  1,
                              )}
                            </span>
                            <span className="esource-builder-settings-modal__canvas-text">
                              <span className="esource-builder-settings-modal__canvas-label">
                                {selected.label || "Untitled"}
                                {selected.required ? (
                                  <span className="mandatory"> *</span>
                                ) : null}
                              </span>
                              <span className="esource-builder-settings-modal__canvas-type">
                                {typeMeta(selected.type).label}
                                {selected.fieldName
                                  ? ` · ${selected.fieldName}`
                                  : ""}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="esource-builder-settings-modal__section-head">
                          <h6 className="esource-builder-settings-modal__section-title">
                            Properties
                          </h6>
                        </div>
                      </div>
                      <div className="esource-builder-settings-modal__col-scroll">
                        <ESourceFieldPropertiesForm
                          field={selected}
                          onChange={updateField}
                          allFields={fields}
                          compact
                        />
                      </div>
                    </section>
                    <section className="esource-builder-settings-modal__col esource-builder-settings-modal__col--logic">
                      <div className="esource-builder-settings-modal__col-top">
                        <div className="esource-builder-settings-modal__section-head">
                          <h6 className="esource-builder-settings-modal__section-title">
                            Conditional logic
                          </h6>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              const item = createEmptyLogicItem();
                              item.actions = [
                                { action: "show", targetId: selected.id },
                              ];
                              setLogic((prev) => [...(prev || []), item]);
                            }}
                          >
                            Add rule
                          </button>
                        </div>
                      </div>
                      <div className="esource-builder-settings-modal__col-scroll">
                        <ESourceConditionalLogicBuilder
                          logic={logic}
                          fields={fields}
                          focusFieldId={selected.id}
                          onChange={setLogic}
                          compact
                          hideToolbar
                        />
                      </div>
                    </section>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeFieldSettings}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={closeFieldSettings}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <CtmsWorkspaceModal
          open={formSettingsOpen && !isReadOnly}
          title="Form settings"
          titleId="esource-form-settings-modal-title"
          onClose={() => setFormSettingsOpen(false)}
          className="esource-builder-form-settings-modal"
        >
          <div className="esource-builder-form-settings">
            <div className="esource-builder-form-settings__field">
              <label
                className="esource-builder-field__label"
                htmlFor="esource-form-header"
              >
                Form header
              </label>
              <input
                id="esource-form-header"
                ref={formHeaderInputRef}
                className="form-control form-control-sm"
                value={formHeader}
                maxLength={120}
                placeholder="e.g. Pharmacy dispensing log"
                onChange={(e) =>
                  setFormHeader(normalizeFormHeader(e.target.value))
                }
              />
              <span className="form-text">
                Title shown at the top when someone opens or fills this form.
                Leave blank if you do not need a title.
              </span>
            </div>
            <div className="esource-builder-form-settings__field">
              <label
                className="esource-builder-field__label"
                htmlFor="esource-form-description"
              >
                Description
                <span className="esource-builder-form-intro__hint">
                  {" "}
                  ({formDescription.length}/{FORM_DESCRIPTION_MAX_CHARS})
                </span>
              </label>
              <textarea
                id="esource-form-description"
                className="form-control form-control-sm"
                rows={3}
                value={formDescription}
                maxLength={FORM_DESCRIPTION_MAX_CHARS}
                placeholder="e.g. Complete all fields before dispensing"
                onChange={(e) =>
                  setFormDescription(normalizeFormDescription(e.target.value))
                }
              />
              <span className="form-text">
                Short guidance under the header for people filling the form.
                Leave blank if no extra guidance is needed.
              </span>
            </div>
            <div className="esource-builder-form-settings__field">
              <span className="esource-builder-field__label">Layout</span>
              <div
                className="esource-builder-layout-picker"
                role="radiogroup"
                aria-label="Form layout"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={
                    (formSettings.layout || "two-column") === "two-column"
                  }
                  className={`esource-builder-layout-picker__option${
                    (formSettings.layout || "two-column") === "two-column"
                      ? " is-active"
                      : ""
                  }`}
                  onClick={() =>
                    setFormSettings(
                      normalizeFormSettings({ layout: "two-column" }),
                    )
                  }
                >
                  <span
                    className="esource-builder-layout-picker__preview esource-builder-layout-picker__preview--two"
                    aria-hidden="true"
                  >
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                  </span>
                  <span className="esource-builder-layout-picker__label">
                    Two column
                  </span>
                  <span className="esource-builder-layout-picker__hint">
                    Fields sit side by side
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={formSettings.layout === "one-column"}
                  className={`esource-builder-layout-picker__option${
                    formSettings.layout === "one-column" ? " is-active" : ""
                  }`}
                  onClick={() =>
                    setFormSettings(
                      normalizeFormSettings({ layout: "one-column" }),
                    )
                  }
                >
                  <span
                    className="esource-builder-layout-picker__preview esource-builder-layout-picker__preview--one"
                    aria-hidden="true"
                  >
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                    <span className="esource-builder-layout-picker__field">
                      <i />
                      <b />
                    </span>
                  </span>
                  <span className="esource-builder-layout-picker__label">
                    One column
                  </span>
                  <span className="esource-builder-layout-picker__hint">
                    Fields stack full width
                  </span>
                </button>
              </div>
            </div>
            <div className="esource-builder-form-settings__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setFormSettingsOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </CtmsWorkspaceModal>

        {previewModal}

        {introPromptIntent ? (
          <div
            className="modal fade show d-block esource-builder-save-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="esource-intro-prompt-title"
            style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="esource-intro-prompt-title">
                    Form header &amp; description
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setIntroPromptIntent(null)}
                  />
                </div>
                <div className="modal-body">
                  <p className="esource-builder-save-modal__hint mb-0">
                    {!String(formHeader || "").trim() &&
                    !String(formDescription || "").trim()
                      ? "Form header and description are empty. Add them for form fillers, or continue without them."
                      : !String(formHeader || "").trim()
                        ? "Form header is empty. Add it for form fillers, or continue without it."
                        : "Description is empty. Add it for form fillers, or continue without it."}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={continueWithoutFormIntro}
                  >
                    Continue without
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={addFormIntroNow}
                  >
                    Add now
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {saveIntent ? (
          <div
            className="modal fade show d-block esource-builder-save-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="esource-save-modal-title"
            style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="esource-save-modal-title">
                    {saveIntent === "publish"
                      ? "Publish template"
                      : isNew
                        ? "Save draft"
                        : "Update form"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeSaveModal}
                  />
                </div>
                <div className="modal-body">
                  <p className="esource-builder-save-modal__hint">
                    Enter the template details before{" "}
                    {saveIntent === "publish"
                      ? "publishing"
                      : isNew
                        ? "saving"
                        : "updating"}
                    .
                  </p>
                  <div className="esource-builder-save-modal__field">
                    <label htmlFor="esource-save-code">
                      Code <span className="mandatory">*</span>
                    </label>
                    <input
                      id="esource-save-code"
                      className="form-control form-control-sm"
                      value={saveCode}
                      disabled={!isNew && !!formTemplateId}
                      onChange={(e) => setSaveCode(e.target.value)}
                      placeholder="e.g. CP05A"
                      autoFocus={isNew || !formTemplateId}
                    />
                  </div>
                  <div className="esource-builder-save-modal__field">
                    <label htmlFor="esource-save-name">
                      Name <span className="mandatory">*</span>
                    </label>
                    <input
                      id="esource-save-name"
                      className="form-control form-control-sm"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Form display name"
                      autoFocus={!isNew && !!formTemplateId}
                    />
                  </div>
                  <div className="esource-builder-save-modal__field">
                    <label>Form type</label>
                    <Dropdown
                      options={FORM_TYPE_OPTIONS}
                      isMulti={false}
                      showSearch
                      selected={
                        FORM_TYPE_OPTIONS.find(
                          (o) => o.value === saveModuleScope,
                        ) || FORM_TYPE_OPTIONS[FORM_TYPE_OPTIONS.length - 1]
                      }
                      onSelect={(opt) =>
                        setSaveModuleScope(opt?.value || OTHER_FORM_TYPE)
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={closeSaveModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      void confirmSaveModal();
                    }}
                  >
                    {saveIntent === "publish"
                      ? "Publish"
                      : isNew
                        ? "Save draft"
                        : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <FaqAssistUnavailableModal
          open={assistUnavailableOpen}
          message={assistUnavailableMessage}
          submitting={assistSupportSubmitting}
          onClose={() => {
            if (!assistSupportSubmitting) setAssistUnavailableOpen(false);
          }}
          onSubmitRequest={handleAssistSupportSubmit}
        />

        <IsfPageTour
          open={showFormBuilderTour}
          steps={formBuilderTourSteps}
          staffId={assistStaffId}
          markTourCompleted={markEsourceFormBuilderTourCompleted}
          onClose={() => setShowFormBuilderTour(false)}
        />

        <InfoAlert
          isOpen={alertState.isOpen}
          onClose={hideAlert}
          title={alertState.title}
          message={alertState.message}
          type={alertState.type}
          icon={alertState.icon}
          buttonText={alertState.buttonText}
          cancelText={alertState.cancelText}
          onConfirm={alertState.onConfirm}
          onCancel={alertState.onCancel}
        />
      </div>
    </ESourceAccessGate>
  );
};

export default ESourceTemplateBuilder;
